import Component from "@base/Component.js";
import authService from "@api/authService.js";
import router from "@/services/router.js";
import t from "@/services/translationService.js";
import LoginModal from "@modules/auth/LoginModal.js";

/**
 * Site header: title, nav links, language switch, login/logout.
 * On narrow screens the nav collapses behind a burger toggle.
 * props: { lang, onLanguageChange(lang) }
 */
export default class Header extends Component {
  constructor(props) {
    super(props);
    this.state = { menuOpen: false };
  }

  render() {
    const { lang } = this.props;
    const { menuOpen } = this.state;
    const authed = authService.isAuthenticated();
    const other = lang === "en" ? "de" : "en";
    return `
      <header class="site-header">
        <a class="brand" href="#/">
          <img class="brand__logo" src="/logo.png" alt="Picturefox" />
          <span class="brand__lockup">
            <span class="brand__name">Picturefox</span>
            <span class="brand__by">by Dario Bolzern</span>
          </span>
        </a>
        <button class="nav-toggle${menuOpen ? " is-active" : ""}" aria-label="${t.t("nav.menu")}" aria-expanded="${menuOpen}">
          <span></span><span></span><span></span>
        </button>
        <nav class="site-nav${menuOpen ? " is-open" : ""}">
          <a href="#/" class="nav-link" data-nav="galleries">${t.t("nav.galleries")}</a>
          <a href="#/gear" class="nav-link">${t.t("nav.showcase")}</a>
          <a href="#/about" class="nav-link">${t.t("nav.about")}</a>
          <a href="#/contact" class="nav-link">${t.t("nav.contact")}</a>
          ${authService.isAdmin() ? `<a href="#/cockpit" class="nav-link nav-link--admin">${t.t("nav.cockpit")}</a>` : ""}
          <button class="lang-switch" data-lang="${other}">${other.toUpperCase()}</button>
          <button class="auth-btn">${authed ? t.t("auth.logout") : t.t("auth.login")}</button>
        </nav>
      </header>`;
  }

  /**
   * "Galleries" points at the home route, which the hero now fills entirely — so
   * arriving there shows the hero, not the galleries. Scroll to the grid instead.
   *
   * Only navigates when actually elsewhere: router.go("/") while already on "/"
   * re-resolves the route, which clears the outlet and rebuilds the panel from
   * scratch, throwing away any tags the visitor has selected.
   */
  async #showGalleries(e) {
    e.preventDefault();
    if (this.state.menuOpen) this.setState({ menuOpen: false });
    if (router.current() !== "/") router.go("/");
    const target = await Header.whenPresent(".gallery-controls");
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  /**
   * Resolve once `selector` exists, or null if it never shows up. Navigating
   * only sets the hash; the router's route handlers are async, so the grid is
   * not in the DOM on the next tick.
   */
  static whenPresent(selector, timeout = 3000) {
    const existing = document.querySelector(selector);
    if (existing) return Promise.resolve(existing);
    return new Promise((resolve) => {
      const done = (el) => {
        observer.disconnect();
        clearTimeout(timer);
        resolve(el);
      };
      const observer = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) done(el);
      });
      observer.observe(document.body, { childList: true, subtree: true });
      const timer = setTimeout(() => done(null), timeout);
    });
  }

  onMounted() {
    this.listen(this.$(".nav-toggle"), "click", () => {
      this.setState({ menuOpen: !this.state.menuOpen });
    });
    this.$$('.nav-link:not([data-nav="galleries"])').forEach((el) => {
      this.listen(el, "click", () => {
        if (this.state.menuOpen) this.setState({ menuOpen: false });
      });
    });
    this.listen(this.$('[data-nav="galleries"]'), "click", (e) => this.#showGalleries(e));
    this.listen(this.$(".lang-switch"), "click", (e) => {
      this.props.onLanguageChange?.(e.currentTarget.getAttribute("data-lang"));
      if (this.state.menuOpen) this.setState({ menuOpen: false });
    });
    this.listen(this.$(".auth-btn"), "click", async () => {
      if (this.state.menuOpen) this.setState({ menuOpen: false });
      if (authService.isAuthenticated()) {
        await authService.logout();
        window.location.reload();
      } else {
        new LoginModal({ onSuccess: () => window.location.reload() }).mount(document.body);
      }
    });
  }
}
